import { Button, Flex } from "@qws-org/ui-kit";
import type { FC } from "react";

import type {
  ActionActionItemUnion,
  ExternalNavigation,
  InternalNavigation,
} from "~/core";

import type { WelcomeOfferBannerProps } from "../WelcomeOfferBanner";

type WelcomeOfferButtonsProps = WelcomeOfferBannerProps;

const isExternalLink = (
  action: ActionActionItemUnion | null | undefined,
): action is ExternalNavigation => {
  return "href" in (action ?? {});
};

const isInternalLink = (
  action: ActionActionItemUnion | null | undefined,
): action is InternalNavigation => {
  return "page" in (action ?? {});
};

type FlexPositionValue = "flex-start" | "center" | "flex-end";

const getFlexPosition = (
  position: string,
): {
  justify: FlexPositionValue;
  align: FlexPositionValue;
} => {
  const [vertical, horizontal] = position.split("-");

  const alignMap: Record<string, FlexPositionValue> = {
    top: "flex-start",
    middle: "center",
    bottom: "flex-end",
  };

  const justifyMap: Record<string, FlexPositionValue> = {
    left: "flex-start",
    center: "center",
    right: "flex-end",
  };

  return {
    align: alignMap[vertical] ?? "center",
    justify: justifyMap[horizontal] ?? "center",
  };
};

export const WelcomeOfferButtons: FC<WelcomeOfferButtonsProps> = ({
  data,
  getUrl,
  onCallAction,
  isActionInactive,
}) => {
  const mControlsPosition = data.controls_position ?? "bottom-center";
  const mdControlsPosition = data.controls_position_tablet ?? mControlsPosition;
  const dControlsPosition =
    data.controls_position_desktop ?? mdControlsPosition;

  const mFlex = getFlexPosition(mControlsPosition);
  const mdFlex = getFlexPosition(mdControlsPosition);
  const dFlex = getFlexPosition(dControlsPosition);

  return (
    <Flex
      align={mFlex.align}
      justify={mFlex.justify}
      gap={16}
      md={{
        justify: mdFlex.justify,
        align: mdFlex.align,
        m: { mt: 0 },
      }}
      d={{ align: dFlex.align, justify: dFlex.justify }}
      m={{ mt: "auto" }}
      p={24}
      height="100%"
    >
      {data.controls
        ?.filter((control) => {
          if (control?.welcome_offer_banner_controls_id?.action) {
            return !isActionInactive(
              control.welcome_offer_banner_controls_id.action,
            );
          }

          return true;
        })
        .map((control, idx) => {
          const controlAction =
            control?.welcome_offer_banner_controls_id?.action;
          const action = controlAction?.action?.[0];
          let href;
          if (isExternalLink(action?.item)) {
            href = action.item.href ?? undefined;
          }
          if (isInternalLink(action?.item)) {
            href = action.item.page ? getUrl(action.item.page) : undefined;
          }
          return (
            <Button
              href={href}
              key={control?.id}
              variant={
                control?.welcome_offer_banner_controls_id?.variant === "primary"
                  ? "default"
                  : "transparent"
              }
              size="small"
              fontWeight="desktop.bold"
              horizontalAlign="center"
              md={{
                p: 18,
                minWidth: 215,
                fontSize: "button.desktop.medium",
                lineHeight: "button.desktop.medium",
              }}
              onPress={() => {
                if (controlAction) {
                  onCallAction(controlAction);
                }
              }}
              p={12}
              minWidth={148}
              data-testid={`welcome-offer-button-${idx}`}
            >
              {control?.welcome_offer_banner_controls_id?.text}
            </Button>
          );
        })}
    </Flex>
  );
};
